import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Truck, Package, Camera, PenLine, Loader2, RefreshCw, CheckCircle2, MapPin, User as UserIcon, LogOut, X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/gov/fulfilment`;

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((r) => r?.msg || JSON.stringify(r)).join('; ');
  if (typeof d === 'string') return d;
  return e?.message || '';
};

// -----------------------------------------------------------------------------
// Signature pad — HTML canvas, works on mouse + touch. Exports a data-URL PNG.
// -----------------------------------------------------------------------------
const SignaturePad = React.forwardRef(({ height = 160 }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [empty, setEmpty] = useState(true);

  React.useImperativeHandle(ref, () => ({
    toDataURL: () => (empty ? null : canvasRef.current.toDataURL('image/png')),
    clear: () => {
      const c = canvasRef.current; if (!c) return;
      c.getContext('2d').clearRect(0, 0, c.width, c.height);
      setEmpty(true);
    },
  }));

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio;
    c.height = height * ratio;
    const ctx = c.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a'; ctx.lineWidth = 2.2;
  }, [height]);

  const posFromEvent = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = posFromEvent(e); setEmpty(false); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = posFromEvent(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
  };
  const end = () => { drawing.current = false; };

  return (
    <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white touch-none" data-testid="signature-pad">
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height, display: 'block' }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
    </div>
  );
});

// -----------------------------------------------------------------------------
// File → data URL helper (compresses via Image + canvas to keep < ~2MB).
// -----------------------------------------------------------------------------
const fileToCompressedDataUrl = (file, maxDim = 1280, quality = 0.75) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// -----------------------------------------------------------------------------
// Delivery page
// -----------------------------------------------------------------------------
const Delivery = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [active, setActive] = useState(null); // order currently being delivered

  useEffect(() => {
    if (loading) return; // wait for AuthContext to hydrate from localStorage
    if (!user) { navigate('/portal/login?next=/delivery'); return; }
    if (user.role !== 'uske_delivery' && user.role !== 'super_admin' && user.role !== 'admin') {
      toast({ title: 'Access denied', description: 'You need the USKE Delivery role.', variant: 'destructive' });
      navigate('/');
    }
  }, [user, loading, navigate]);

  const load = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const r = await axios.get(`${API}/orders`, { params: { status: 'in_transit' } });
      setOrders(r.data || []);
    } catch (e) {
      toast({ title: 'Load failed', description: errMsg(e), variant: 'destructive' });
    } finally { setLoadingOrders(false); }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-blue-900 text-white sticky top-0 z-30 shadow" data-testid="delivery-header">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="p-1.5 bg-white/10 rounded-md"><Truck className="h-5 w-5" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold leading-tight">USKE Delivery</div>
            <div className="text-[11px] text-blue-200 truncate">{user?.name || '—'} · {user?.email || ''}</div>
          </div>
          <Button size="sm" variant="ghost" onClick={load} disabled={loadingOrders} className="text-white hover:bg-white/10" data-testid="delivery-refresh">
            <RefreshCw className={`h-4 w-4 ${loadingOrders ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { logout(); navigate('/portal/login'); }} className="text-white hover:bg-white/10" data-testid="delivery-logout">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-3 py-4 pb-24 space-y-3">
        {loadingOrders && orders.length === 0 ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>
        ) : orders.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            <Package className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <div className="text-base font-medium">All caught up.</div>
            <div className="text-sm">No deliveries in transit right now.</div>
          </div>
        ) : (
          orders.map((o) => (
            <OrderCard key={o.order_id} order={o} onDeliver={() => setActive(o)} />
          ))
        )}
      </main>

      {active && (
        <DeliverySheet order={active} onClose={() => setActive(null)} onDelivered={() => { setActive(null); load(); }} />
      )}
    </div>
  );
};

const OrderCard = ({ order, onDeliver }) => (
  <Card className="border-2" data-testid={`delivery-card-${order.order_id}`}>
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-amber-100 text-amber-800 rounded-md"><Truck className="h-5 w-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-800">{order.order_id}</div>
          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
            <UserIcon className="h-3 w-3" /> {order.purchaser_name || order.purchaser_email || '—'}
          </div>
          <div className="text-xs text-slate-500 flex items-start gap-1 mt-1">
            <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
            <span className="line-clamp-2">
              {order.delivery_location?.name ? `${order.delivery_location.name} — ` : ''}
              {order.delivery_location?.address_line1 || order.shipping_address || '—'}
              {order.delivery_location?.city ? `, ${order.delivery_location.city}` : ''}
              {order.delivery_location?.state ? ` ${order.delivery_location.state}` : ''}
            </span>
          </div>
          <div className="text-xs text-slate-600 mt-1">{order.item_count} item(s) · <b>{money(order.total)}</b></div>
          {order.tracking_number && <div className="text-[11px] text-slate-500 mt-0.5">Tracking: {order.tracking_number}</div>}
        </div>
      </div>
      <div className="mt-3">
        <Button onClick={onDeliver} size="sm" className="w-full bg-green-700 hover:bg-green-800 text-white" data-testid={`btn-deliver-${order.order_id}`}>
          <CheckCircle2 className="h-4 w-4 mr-1" /> Mark Delivered
        </Button>
      </div>
    </CardContent>
  </Card>
);

const DeliverySheet = ({ order, onClose, onDelivered }) => {
  const [recipientName, setRecipientName] = useState('');
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [geo, setGeo] = useState(null); // { lat, lng, accuracy }
  const [geoStatus, setGeoStatus] = useState('idle'); // idle | requesting | ok | denied | unsupported
  const sigRef = useRef(null);

  // Auto-request geolocation the moment the sheet opens.
  useEffect(() => {
    if (!('geolocation' in navigator)) { setGeoStatus('unsupported'); return; }
    setGeoStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGeoStatus('ok');
      },
      () => setGeoStatus('denied'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  }, []);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const dataUrl = await fileToCompressedDataUrl(f);
      setPhoto(dataUrl);
    } catch (err) {
      toast({ title: 'Photo failed', description: err?.message || 'Could not process photo', variant: 'destructive' });
    }
  };

  const submit = async () => {
    if (!recipientName.trim()) {
      toast({ title: 'Recipient name required', variant: 'destructive' }); return;
    }
    const sig = sigRef.current?.toDataURL();
    if (!photo && !sig) {
      toast({ title: 'Need a photo or signature', description: 'Please attach at least one proof.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/orders/${order.order_id}/mark-delivered`, {
        recipient_name: recipientName.trim(),
        notes: notes.trim() || null,
        photo_data_url: photo || null,
        signature_data_url: sig || null,
        lat: geo?.lat, lng: geo?.lng, geo_accuracy_m: geo?.accuracy,
      });
      toast({ title: 'Delivered', description: `Order ${order.order_id} marked delivered.` });
      onDelivered();
    } catch (e) {
      toast({ title: 'Delivery failed', description: errMsg(e), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="delivery-sheet">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-2xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white px-4 py-3 border-b flex items-center gap-2">
          <div className="font-semibold text-slate-800 flex-1">Deliver {order.order_id}</div>
          <Button size="icon" variant="ghost" onClick={onClose} data-testid="delivery-sheet-close"><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-4 space-y-4">
          <div className="text-xs text-slate-500">
            Delivering to <b className="text-slate-700">{order.delivery_location?.name || order.shipping_address || '—'}</b>
          </div>

          <div>
            <Label className="text-xs">Recipient name *</Label>
            <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="J. Smith" className="mt-1" data-testid="delivery-recipient" />
          </div>

          <div>
            <Label className="text-xs flex items-center gap-1"><Camera className="h-3.5 w-3.5" /> Delivery photo</Label>
            <input type="file" accept="image/*" capture="environment" onChange={onPhoto}
                   className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                   data-testid="delivery-photo-input" />
            {photo && (
              <div className="mt-2 relative">
                <img src={photo} alt="delivery proof" className="max-h-48 rounded border w-full object-cover" />
                <Button size="sm" variant="secondary" onClick={() => setPhoto(null)} className="absolute top-1 right-1" data-testid="delivery-photo-remove">
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs flex items-center gap-1"><PenLine className="h-3.5 w-3.5" /> Recipient signature</Label>
            <div className="mt-1">
              <SignaturePad ref={sigRef} height={140} />
            </div>
            <Button size="sm" variant="outline" onClick={() => sigRef.current?.clear()} className="mt-1 text-xs" data-testid="delivery-signature-clear">Clear signature</Button>
          </div>

          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Left at reception" className="mt-1" data-testid="delivery-notes" />
          </div>

          {/* Geotag status */}
          <div className="text-[11px] text-slate-500 flex items-center gap-1 -mt-1" data-testid="delivery-geo-status">
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${geoStatus === 'ok' ? 'bg-green-500' : geoStatus === 'requesting' ? 'bg-amber-500' : 'bg-slate-400'}`} />
            {geoStatus === 'ok' && geo ? `Geotag captured (${geo.lat.toFixed(5)}, ${geo.lng.toFixed(5)} · ±${Math.round(geo.accuracy)}m)`
              : geoStatus === 'requesting' ? 'Requesting location…'
              : geoStatus === 'denied' ? 'Location denied — POD will be recorded without geotag.'
              : geoStatus === 'unsupported' ? 'Location unavailable on this device.'
              : ''}
          </div>
        </div>
        <div className="sticky bottom-0 bg-white p-3 border-t flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="flex-1 bg-green-700 hover:bg-green-800 text-white" data-testid="delivery-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
            Confirm delivery
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Delivery;
